import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { buildDetailPath, buildSectionPath, useAppNavigate } from '../../utils/Routing';
import {
  HeroBar, StatTile, YearChart,
  CategoryRankRow, FriendRankRow, EventRow,
} from './DetailViewParts';
import { EntityEvents } from './EntityEvents';
import {
  buildYearBreakdown, findMostActiveYear, rankByEvents,
  findTopPlace, buildRecentEvents, getEventStartAt,
  countDistinctPlaces, formatLongDate,
} from './insightHelpers';
import './DetailView.scss';

export function TagDetailView({entityId, tab = 'summary'}) {
  const tags = useSelector(state => state.viewingUserTags) || {};
  const friends = useSelector(state => state.viewingUserFriends) || {};
  const categories = useSelector(state => state.viewingUserCategories) || {};
  const trips = useSelector(state => state.viewingUserTrips) || {};
  const events = useSelector(state => state.viewingUserEvents) || {};
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state => state.loggedInUserAccount);
  const isOwnMap = viewingUserAccount && loggedInUserAccount
    && viewingUserAccount.id === loggedInUserAccount.id;
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  const tag = tags[entityId];
  const username = viewingUserAccount?.username;

  useEffect(() => {
    if(!tag && username){
      navigateTo(buildSectionPath(username, 'Tags'));
    }
  }, [tag, username, navigateTo]);

  // Events tagged with this tag — root canvas for every insight.
  const tagEvents = useMemo(() => {
    return Object.values(events).filter(e => (e.tagIds || []).includes(entityId));
  }, [events, entityId]);

  const yearEntries = useMemo(() => buildYearBreakdown(tagEvents), [tagEvents]);
  const mostActiveYear = useMemo(() => findMostActiveYear(tagEvents), [tagEvents]);
  const topCategories = useMemo(
    () => rankByEvents(tagEvents, e => e.categoryId, categories),
    [tagEvents, categories]
  );
  const topFriends = useMemo(
    () => rankByEvents(tagEvents, e => e.friendIds, friends),
    [tagEvents, friends]
  );
  const topTrips = useMemo(
    () => rankByEvents(tagEvents, e => e.tripIds, trips),
    [tagEvents, trips]
  );
  const topPlace = useMemo(() => findTopPlace(tagEvents), [tagEvents]);
  const placeCount = useMemo(() => countDistinctPlaces(tagEvents), [tagEvents]);
  const recentEvents = useMemo(() => buildRecentEvents(tagEvents, 6), [tagEvents]);

  const sortedDates = useMemo(() => {
    return tagEvents.map(getEventStartAt).filter(Boolean).sort((a, b) => a - b);
  }, [tagEvents]);
  const firstDate = sortedDates[0] || null;
  const lastDate = sortedDates[sortedDates.length - 1] || null;
  const yearsCovered = firstDate && lastDate
    ? Math.max(1, lastDate.getFullYear() - firstDate.getFullYear() + 1)
    : 0;

  if(!tag){
    return (
      <main className="detail-view">
        <div className="detail-view__not-found">
          <div>This tag couldn't be found.</div>
        </div>
      </main>
    );
  }

  const goBack = () => {
    if(!username) return;
    navigateTo(buildSectionPath(username, 'Tags'));
  };

  const selectTab = (nextTab) => {
    if(!username || nextTab === tab) return;
    navigateTo(buildDetailPath(username, 'Tags', tag.id, nextTab));
  };

  const openEdit = () => {
    dispatch(setDialogOpen('Edit Tag', {
      parentDialog: '',
      listDialog: null,
      dialogTitle: `Edit Tag: ${tag.name}`,
      tag,
    }));
  };

  const openNewEvent = () => {
    if(!loggedInUserAccount) return;
    const categoryList = Object.values(categories);
    if(categoryList.length === 0){
      alert('Create a category first.');
      return;
    }
    const newEvent = {
      userId: loggedInUserAccount.id,
      title: '',
      categoryId: categoryList[0].id,
      tagIds: [tag.id],
    };
    dispatch(setDialogOpen('Edit Event', {event: newEvent}));
  };

  const openEvent = (event) => {
    dispatch(setSelectedEvent(event));
    dispatch(setDialogOpen('View Event', {event}));
  };

  const openFriend = (friend) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/friends/${encodeURIComponent(friend.id)}`);
  };

  const openCategory = (category) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/categories/${encodeURIComponent(category.id)}`);
  };

  const openTrip = (trip) => {
    if(!username) return;
    navigateTo(`/${encodeURIComponent(username)}/trips/${encodeURIComponent(trip.id)}`);
  };

  return (
    <main className={`detail-view ${tab === 'events' ? 'detail-view--events' : ''}`.trim()}>
      <section className={`detail-view__hero ${tab === 'events' ? 'detail-view__hero--collapsed' : ''}`.trim()}>
        <div className="detail-view__hero-glow detail-view__hero-glow--tag" aria-hidden="true"/>
        <HeroBar
          backLabel="All Tags"
          onBack={goBack}
          onNewEvent={isOwnMap ? openNewEvent : undefined}
          onEdit={isOwnMap ? openEdit : undefined}
          editLabel="Edit Tag"
          entityTitle={`#${tag.name}`}
          tab={tab}
          onSelectTab={selectTab}
        />
        <div className="detail-view__hero-content">
          <div
            className="detail-view__hero-icon"
            style={{background:'linear-gradient(135deg, #ec4899, #8b5cf6)', color:'#ffffff'}}
            aria-hidden="true"
          >
            #
          </div>
          <div className="detail-view__hero-tagline">Tag</div>
          <h1 className="detail-view__hero-title">#{tag.name}</h1>
          <div className="detail-view__hero-subtitle">
            {tagEvents.length > 0
              ? `${tagEvents.length} ${tagEvents.length === 1 ? 'event' : 'events'}${yearsCovered > 0 ? ` over ${yearsCovered} ${yearsCovered === 1 ? 'year' : 'years'}` : ''}`
              : 'No events with this tag yet'}
          </div>
        </div>
      </section>

      {tab === 'events' &&
        <EntityEvents
          events={tagEvents}
          emptyText={`Tag events with #${tag.name} and they'll appear here.`}
        />
      }

      {tab === 'summary' &&
      <>
      <section className="detail-view__stats-grid" aria-label="Tag stats">
        <StatTile
          label="Events"
          value={tagEvents.length}
          accent="#ec4899"
          icon="🏷️"
          delay={120}
        />
        <StatTile
          label="Categories"
          value={topCategories.length}
          accent="#10b981"
          icon="🗂️"
          delay={200}
        />
        <StatTile
          label="Friends"
          value={topFriends.length}
          accent="#f59e0b"
          icon="👥"
          delay={280}
        />
        <StatTile
          label="Places"
          value={placeCount}
          accent="#0ea5e9"
          icon="📍"
          delay={360}
        />
      </section>

      {yearEntries.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Events by Year</h2>
            {mostActiveYear && mostActiveYear[1] > 0 &&
              <span className="detail-view__panel-subtitle">
                Peaked in {mostActiveYear[0]} ({mostActiveYear[1]} {mostActiveYear[1] === 1 ? 'event' : 'events'})
              </span>
            }
          </header>
          <YearChart entries={yearEntries} accentTop="#f472b6" accentBottom="#ec4899"/>
        </section>
      }

      {(topCategories.length > 0 || topFriends.length > 0) &&
        <section className="detail-view__two-up">
          {topCategories.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Categories</h2>
              </header>
              <div className="detail-view__rank-list">
                {topCategories.map((item, idx) => (
                  <CategoryRankRow
                    key={item.entity.id}
                    category={item.entity}
                    count={item.count}
                    percent={(item.count / topCategories[0].count) * 100}
                    delay={400 + idx * 80}
                    onClick={() => openCategory(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
          {topFriends.length > 0 &&
            <div className="detail-view__panel">
              <header className="detail-view__panel-header">
                <h2>Top Friends</h2>
              </header>
              <div className="detail-view__rank-list">
                {topFriends.map((item, idx) => (
                  <FriendRankRow
                    key={item.entity.id}
                    friend={item.entity}
                    count={item.count}
                    percent={(item.count / topFriends[0].count) * 100}
                    delay={500 + idx * 80}
                    onClick={() => openFriend(item.entity)}
                  />
                ))}
              </div>
            </div>
          }
        </section>
      }

      {(topTrips.length > 0 || firstDate || topPlace) &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Highlights</h2>
          </header>
          <div className="detail-view__rank-list">
            {firstDate &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#10b981'}}>✨</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">First tagged</span>
                    <span className="detail-view__rank-count">{formatLongDate(firstDate)}</span>
                  </div>
                </div>
              </div>
            }
            {lastDate && lastDate.getTime() !== firstDate?.getTime() &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#f59e0b'}}>🕒</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Most recent</span>
                    <span className="detail-view__rank-count">{formatLongDate(lastDate)}</span>
                  </div>
                </div>
              </div>
            }
            {topTrips.length > 0 &&
              <button
                type="button"
                className="detail-view__rank-row detail-view__rank-row--clickable"
                onClick={() => openTrip(topTrips[0].entity)}
              >
                <div className="detail-view__rank-icon" style={{backgroundColor:'#0ea5e9'}}>🧳</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Top trip</span>
                    <span className="detail-view__rank-count">{topTrips[0].count}</span>
                  </div>
                  <div className="detail-view__event-meta" style={{marginTop:0}}>
                    {topTrips[0].entity.name}
                  </div>
                </div>
              </button>
            }
            {topPlace &&
              <div className="detail-view__rank-row">
                <div className="detail-view__rank-icon" style={{backgroundColor:'#6366f1'}}>📍</div>
                <div className="detail-view__rank-body">
                  <div className="detail-view__rank-label-row">
                    <span className="detail-view__rank-name">Most-visited place</span>
                    <span className="detail-view__rank-count">{topPlace.count}</span>
                  </div>
                  <div className="detail-view__event-meta" style={{marginTop:0}}>
                    {topPlace.description}
                  </div>
                </div>
              </div>
            }
          </div>
        </section>
      }

      {recentEvents.length > 0 &&
        <section className="detail-view__panel">
          <header className="detail-view__panel-header">
            <h2>Recent #{tag.name} Events</h2>
            <span className="detail-view__panel-subtitle">
              {tagEvents.length} total
            </span>
          </header>
          <div className="detail-view__events-list">
            {recentEvents.map((item, idx) => (
              <EventRow
                key={item.event.id}
                event={item.event}
                startAt={item.startAt}
                category={categories[item.event.categoryId]}
                onClick={() => openEvent(item.event)}
                delay={120 + idx * 50}
              />
            ))}
          </div>
        </section>
      }

      {tagEvents.length === 0 &&
        <section className="detail-view__empty">
          <div className="detail-view__empty-title">No events with this tag yet</div>
          <div>Tag events with #{tag.name} and they'll appear here.</div>
        </section>
      }
      </>
      }
    </main>
  );
}
